<template>
    <!-- Card and header for Rules table -->
    <b-card body-class="p-0" header-class="border-0" class="mb-4">
        <template v-slot:header>
            <b-row align-v="center">
                <b-col v-if="!compare">
                    <h3 class="mb-0">Rules</h3>
                </b-col>
                <b-col v-else>
                    <h3 class="mb-0">
                        HALO Passed:
                        <span :class="getResultText.style">
                            {{ getResultText.text }}
                        </span>
                    </h3>
                </b-col>
                <b-col cols="5" class="text-right">
                    <span class="label mr-5">Show Passing:</span>
                    <b-form-checkbox v-model="showPassing" name="check-button" class="ml--2 mr--4 pt-2" inline switch></b-form-checkbox>
                </b-col>
            </b-row>
        </template>

        <!-- Rules Table -->
        <transition-group name="slide-in">
            <el-table
                class="table-responsive table"
                :data="getRuleData"
                header-row-class-name="thead-light outer-table"
                header-cell-class-name="outer-table"
                cell-class-name="outer-table"
                fit
                :key="`${showPassing} ${getRulesAnimationState}`"
                :empty-text="getEmptyText"
            >
                
                <!-- expandable row button -->
                <el-table-column type="expand">
                    <template slot-scope="props">
                        <b-container>
                            <b-row class="ml-3">
                                    <h5>Actual Values</h5>
                            </b-row>
                            <b-row class="ml-2 mr-5">
                                <el-table
                                    :data="props.row.fields"
                                    class="table-responsive"
                                    header-row-class-name="thead-light"
                                    header-cell-class-name="inner-table"
                                    :row-class-name="tableRowClassName"
                                    cell-class-name="inner-table"
                                    fit
                                    :empty-text="getEmptyText"
                                    border
                                >
                                        <el-table-column 
                                            label="field"
                                            prop="name"
                                        >
                                        </el-table-column>

                                        <el-table-column 
                                            label="value"
                                            prop="value"
                                        >
                                        
                                        </el-table-column>
                                </el-table>
                            </b-row>
                        </b-container>
                    </template>
                </el-table-column>

                <!-- Name column -->
                <el-table-column label="Name" min-width="100%">
                    <template v-slot="{ row }">
                        <div class="table-row-name" :key="row.name">{{ row.name }}</div>
                    </template>
                </el-table-column>

                <!-- Requirements column -->
                <el-table-column label="Requirements" prop="message" min-width="150%">
                    <template v-slot="{ row }">
                        <div class="allow-new-lines">{{ row.message }}</div>
                    </template> 
                </el-table-column>
                
                <!-- Fields column -->
                <!-- <el-table-column label="Actual Value" prop="fields">
                    <template v-slot="{ row }">
                        <div class="allow-new-lines">{{ row.fields }}</div>
                    </template>
                </el-table-column> -->

                <!-- Status column -->
                <el-table-column label="Status" prop="status">
                    <template v-slot="{ row }">
                        <span :class="`mr-2 text-${row.variant}`"><i :class="getIcon(row.status)"></i></span>
                        <span>{{ row.status }}</span>
                    </template>
                </el-table-column>

                <!-- Exception Button column -->
                <el-table-column min-width="40%">
                    <template v-slot="{ row }">
                        <el-dropdown
                            trigger="click"
                            class="dropdown"
                            v-if="row.status !== 'Passed' && row.status !== 'Insufficient factors' && scenarioName !== 'toolScenario' && ((compare && row.override) || (!compare && (checkUserPermissions(row) && getHasRun) || row.override))"
                            @command="onCommandDropdown"
                            :data-test="row.status"
                        >
                            <span class="btn btn-sm btn-icon-only text-light">
                                <i class="fas fa-ellipsis-v mt-2"></i>
                            </span>
                            <el-dropdown-menu class="dropdown-menu dropdown-menu-arrow show" slot="dropdown">
                                <!-- non-legacy loans -->
                                <!-- view an exception with the ability to overwrite it. only available in sections if not an old exception that no longer has enough comp factors. Contains exception type and category -->
                                <el-dropdown-item v-b-modal.sections :command="{reconfirm: false, name: row.actualName}" v-if="!compare && !getIsLegacyLoan">
                                    {{ row.override ? "View Exception" : "Make Exception" }}
                                </el-dropdown-item>
                                <!-- view an exception without being able to overwrite it. available in compare scenarios and for old exceptions that no longer have enough comp factors and requires a scenarioName -->
                                <el-dropdown-item v-b-modal="`view-${scenarioName}`" :command="{reconfirm: false, name: row.actualName}" v-if="row.override && !getIsLegacyLoan && compare">
                                    View Exception
                                </el-dropdown-item>
                                <!-- reconfirm an exception without having to bring up the modal -->
                                <el-dropdown-item v-if="row.status === 'Reconfirm'" :command="{reconfirm: true, name: row.actualName}">
                                    Reconfirm Exception
                                </el-dropdown-item>
                                
                                <!-- Legacy Loans -->
                                <!-- view an exception with the ability to overwrite it. only available in sections. Legacy exceptions -->
                                <el-dropdown-item v-b-modal="'legacy-sections'" :command="{reconfirm: false, name: row.actualName}" v-if="!compare && getIsLegacyLoan">
                                    {{ row.override ? "View Exception" : "Make Exception" }}
                                </el-dropdown-item>
                                <!-- view an exception without being able to overwrite it. available in compare scenarios and requires a scenarioName. Legacy exceptions-->
                                <el-dropdown-item v-b-modal="`view-legacy-${scenarioName}`" :command="{reconfirm: false, name: row.actualName}" v-if="row.override && compare && getIsLegacyLoan">
                                    View Exception
                                </el-dropdown-item>
                            </el-dropdown-menu>
                        </el-dropdown>
                        <div v-else-if="row.status !== 'Passed' && !compare && scenarioName !== 'toolScenario'" class="ml-2">

                            <el-popover trigger="hover" placement="top" :content="getPopoverText(row)" width="200" >
                                <i class="fas fa-exclamation-circle text-ao-light" slot="reference"></i>
                            </el-popover>
                        </div>
                    </template>
                </el-table-column>
            </el-table>
        </transition-group>

        <!-- Non-legacy exceptions -->
        <!-- sections exception modal -->
        <!-- TODO: make the ok button text change happen after the modal closes -->
        <b-modal
            centered
            :ok-title="getOkButtonText(getSelectedRule.override)"
            id="sections"
            size="xl"
            @ok="getSelectedRule.override ? updateExceptionRule($event) : updateFailedRule($event)"
            @hidden="
                setCurrentOverride();
                setPassingCompFactorLength();
            "
            :ok-variant="!getHasRun || !checkUserPermissions(getSelectedRule) ? 'light' : 'ao-light'"
            :static="staticModal"
            :ok-disabled="!getHasRun || !checkUserPermissions(getSelectedRule)"
        >
            <h3 slot="modal-title">Exception Entry Form</h3>
            <Exception :disabled="false" />
        </b-modal>
        
        <!-- view exception modal (compare) -->
        <!-- TODO: make the ok button text change happen after the modal closes -->
        <b-modal
            centered
            ok-title="Close"
            :id="`view-${scenarioName}`"
            size="xl"
            @ok="$bvModal.hide(`view-${scenarioName}`)"
            ok-only
            ok-variant="secondary"
            :static="staticModal"
        >
            <h3 slot="modal-title">View Exception Form</h3>
            <Exception :disabled="true" />
        </b-modal>

        <!-- Legacy Exceptions -->
        <!-- TODO: make the ok button text change happen after the modal closes. and test ok title -->
        <b-modal
            centered
            :ok-title="getOkButtonText(getSelectedRule.override)"            
            id="legacy-sections"
            size="xl"
            @ok="getSelectedRule.override ? updateExceptionRule($event) : updateFailedRule($event)"
            @hidden="
                setCurrentOverride();
            "
            ok-variant="ao-light"
            :static="staticModal"
        >
            <h3 slot="modal-title">Exception Entry Form</h3>
            <LegacyException :disabled="false" />
        </b-modal>

        <!-- TODO: make the ok button text change happen after the modal closes. and test ok title -->
        <b-modal
            centered
            ok-title="Close"
            :id="`view-legacy-${scenarioName}`"
            size="xl"
            @ok="$bvModal.hide(`view-legacy-${scenarioName}`)"
            ok-only
            ok-variant="secondary"
            :static="staticModal"
        >
            <h3 slot="modal-title">View Exception Form</h3>
            <LegacyException :disabled="true" />
        </b-modal>
    </b-card>
</template>
<script>
import { Table, TableColumn, DropdownMenu, DropdownItem, Dropdown } from "element-ui";
import Exception from "./Exception";
import LegacyException from "./LegacyException";
import { mapGetters, mapMutations } from "vuex";
import useVuelidate from "@vuelidate/core";
import { ruleDialog } from "@/mixins/dialog.js";
import { convertToString } from "@/utils/DateConverter.js"
import { addFieldToList } from "@/utils/FieldFormatter.js"
import { requiredIf } from "vuelidate/lib/validators";

export default {
    name: "rules-table",
    components: {
        ["ElTable"]: Table,
        ["ElTableColumn"]: TableColumn,
        ["ElDropdown"]: Dropdown,
        ["ElDropdownItem"]: DropdownItem,
        ["ElDropdownMenu"]: DropdownMenu,
        Exception,
        LegacyException
    },
    props: {
        compare: Boolean,
        scenarioName: String,
    },
    setup() {
        return {
            v$: useVuelidate(),
        };
    },
    data() {
        return {
            showPassing: false,
            rulesAnimationToggle: false,
            staticModal: false,
        };
    },
    computed: {
        ...mapGetters([
            "getScenarioByName", 
            "getSelectedRule", 
            "getCurrentOverride", 
            "getRulesAnimationState", 
            "getPassingCompFactorLength",
            "userIsUnderwriter",
            "userIsManager",
            "getHasRun",
            "getIsLegacyLoan",
            "getUsernames"
        ]),

        /**
         * gets the data for all of the rules and format it to be displayed in the table
         * @returns an array of rules in the scenario
         * each object in the array has the following fields
         * @field name: the name of the rule to be displayed in the table
         * @field message: the message of the rule to be displayed in the table
         * @field fields: the fields of the rule. If the rule is nested then this includes the
         *        breadcrumbs leading to the value being evaluated
         * @field status: the status of the rule (i.e passing, failing, exception, etc.)
         * @field variant: this is the variant used for styling the status messages
         * @field actualName: the acutal name of the rule used for performing actions on the rule.
         *        Added to ensure that the rule is found by name even if the name is altered due to
         *        being a beta rule.
         * @field override: holds exception information if there is any.
         * @field exceptionLevel: the exception level of the rule. dictates the requirements to make an exception
         */
        getRuleData() {
            // get rule data to be displayed in the table
            this.convertRuleDates(this.scenarioName);
            const tableData = [];
            console.log(this.getScenarioByName(this.scenarioName).rules);
            // for each rule in the scenario generate the list of values and the status
            for (let rule of this.getScenarioByName(this.scenarioName).rules) {
                let fields = [];
                // generate the message to be displayed for each field
                for (let field of rule.fields) {
                    // add the fields recursively if the field has children
                    if (field.children) {
                        fields = addFieldToList(field, fields, []);
                    // if the field does not have children just add the name and value to the list
                    } else {
                        fields.push({
                            name: field.name,
                            value: field.value ?? 'NO VALUE',
                            valid: field.valid
                        });
                    }
                }
                // sets the status and variant (state of results) of the rule
                let status;
                let variant;
                // if the rule passes
                if (rule.valid) {
                    status = "Passed";
                    variant = "success";
                // if the rule fails with no exception
                } else if (!rule.override) {
                    status = "Failed";
                    variant = "danger";
                // if the rule failed and has an exception but there are not enough comp factors for the exception to be submitted
                } else if (this.getPassingCompFactorLength < 2 && rule.exceptionLevel >= 2 && !this.compare) {
                    status = "Insufficient factors";
                    variant = "warning";
                // if the rule failed and has a confirmed exception or confirmed has not been set on loading scenario
                } else if (rule.override.confirmed || rule.override.confirmed === undefined) {
                    status = "Exception";
                    variant = "primary";
                // if the rule failed and has an exception from a previous scenario that has not been confirmed for the new scenario
                } else if (!this.compare) {
                    status = "Reconfirm";
                    variant = "default";
                }
                const ruleData = {
                    name: rule.beta ? "Beta: " + rule.name : rule.name,
                    message: rule.message,
                    fields,
                    status,
                    variant,
                    actualName: rule.name,
                    override: rule.override,
                    exceptionLevel: rule.exceptionLevel ?? null,
                };
                // push the rule data to be displayed in the table if the rule is not passing or if show passing rules is toggled
                if (this.showPassing || ruleData.status !== "Passed") {
                    tableData.push(ruleData);
                }
            }
            return tableData;
        },
        getResultText() {
            const valid = this.getScenarioByName(this.scenarioName).valid;
            let result = {
                text: "",
                style: "",
            };
            if (valid) {
                result.text = "Success";
                result.style = "pass";
            } else if (valid === false) {
                result.text = "Failure";
                result.style = "fail";
            } else {
                result.text = "Save to see result";
                result.style = "";
            }
            return result;
        },
        getEmptyText() {
            return this.getScenarioByName(this.scenarioName).rules.length > 0 ? "No Failed Rules" : "No scenario has been run or saved.";
        }
    },
    methods: {
        ...mapMutations([
            "setSelectedRule",
            "setRuleOverride",
            "updateSelectedRule",
            "setCurrentOverride",
            "convertRuleDates",
            "setPassingCompFactorLength",
            "setSaved"
        ]),
        /**
         * @param command an object with two fields:
         *      @field {boolean} reconfirm: states whether the reconfirm item was clicked
         *      @field {String} name: the name of the rule the selected exception belongs to
         */
        onCommandDropdown(command) {
            this.setSelectedRule({ scenario: this.scenarioName, row: command.name });
            // if the reconfirm option was selected
            if (command.reconfirm) {
                this.setCurrentOverride({
                    username: this.getSelectedRule.override.username,
                    created: convertToString(this.getSelectedRule.override.created),
                    userId: this.getSelectedRule.override.userId,
                    comment: this.getSelectedRule.override.comment,
                    grantedByUserId: this.getSelectedRule.override.grantedByUserId,
                    grantedByUsername: this.getSelectedRule.override.grantedByUsername,
                    grantedByDate: convertToString(this.getSelectedRule.override.grantedByDate),
                    category: this.getSelectedRule.override.category,
                    type: this.getSelectedRule.override.type,
                    confirmed: this.getSelectedRule.override.confirmed,
                    sendToCrse: this.getSelectedRule.override.sendToCrse
                });
                // true is sent to indicate that the rule is being updated from the reconfirm dropdown option
                this.updateExceptionRule(this.getCurrentOverride, true);
            // if the reconfirm option was not selected (currently means make or view option was selected)
            } else {
                // Legacy Loans
                if (this.getIsLegacyLoan) {
                    // if the legacy loan's selected rule has an override
                    if (!this.getSelectedRule.override) {
                        this.setCurrentOverride({
                            username: this.getScenarioByName(this.scenarioName).username,
                            created: this.getCurrentDate(this.getSelectedRule.override),
                            userId: this.getScenarioByName(this.scenarioName).userId,
                            comment: "",
                            sendToCrse: false
                        });
                    } else {
                        this.setCurrentOverride({
                            username: this.getSelectedRule.override.username,
                            created: convertToString(this.getSelectedRule.override.created),
                            userId: this.getSelectedRule.override.userId,
                            comment: this.getSelectedRule.override.comment,
                            sendToCrse: false
                        });
                    }
    
                // Non-legacy loans
                } else {
                    if (!this.getSelectedRule.override) {
                        // placeholders for grantedByUser fields if they exist in the list of users
                        let grantedByName = "";
                        let grantedById = "";
                        let grantedDate = null;
                        const scenarioUsername = this.getScenarioByName(this.scenarioName).username;
                        // check if grantedBy fields are required
                        if (this.getSelectedRule.exceptionLevel >= 2) {
                            // populate with today's date
                            grantedDate = this.getCurrentDate(this.getSelectedRule.override);
                            // check if the user's name is in the granted by list
                            if (this.getUsernames.includes(scenarioUsername)) {
                                // if the name is in the list then set the placeholder equal to the user's name
                                grantedByName = scenarioUsername
                                grantedById = this.getScenarioByName(this.scenarioName).userId;
                            }
                        }
    
                        // grantedByUsername and grantedByUserId are only prepopulated if the user's name exists in the list of granted by users
                        this.setCurrentOverride({
                            username: this.getScenarioByName(this.scenarioName).username,
                            created: this.getCurrentDate(this.getSelectedRule.override),
                            userId: this.getScenarioByName(this.scenarioName).userId,
                            grantedByUserId: grantedById,
                            grantedByUsername: grantedByName,
                            grantedByDate: grantedDate,
                            comment: "",
                            category: "",
                            type: "",
                            confirmed: false,
                            sendToCrse: this.getSelectedRule.exceptionLevel === 1 ? false : true
                        });
                    } else {
                        this.setCurrentOverride({
                            username: this.getSelectedRule.override.username,
                            created: convertToString(this.getSelectedRule.override.created),
                            userId: this.getSelectedRule.override.userId,
                            comment: this.getSelectedRule.override.comment,
                            grantedByUserId: this.getSelectedRule.override.grantedByUserId,
                            grantedByUsername: this.getSelectedRule.override.grantedByUsername,
                            grantedByDate: convertToString(this.getSelectedRule.override.grantedByDate),
                            category: this.getSelectedRule.override.category,
                            type: this.getSelectedRule.override.type,
                            confirmed: this.getSelectedRule.override.confirmed,
                            sendToCrse: this.getSelectedRule.override.sendToCrse
                        });
                    }
                }
            }
        },

        updateFailedRule(ev) {
            // used for validating override comment and updating the selected rule with the override data if the rule does not have a pre-existing exception
            this.v$.$touch();
            this.alertOnInvalidFields(this.v$, ev, false);
            if (!this.v$.$invalid) {
                const createdDate = new Date(this.getCurrentOverride.created);
                let override = Object.assign({}, this.getCurrentOverride);
                override["created"] = createdDate.valueOf();
                // if not a legacy loan also push granted by date
                if (!this.getIsLegacyLoan) {
                    const grantedByDate = new Date(this.getCurrentOverride.grantedByDate);
                    override["grantedByDate"] = grantedByDate.valueOf();
                }
                override["confirmed"] = true;
                this.setRuleOverride(override);
                this.updateSelectedRule();
                this.setSaved(false);
            }
        },
        updateExceptionRule(ev, reconfirmFromDropdown) {
            // used for validatiing override comment and updating the selected rule with the override data if the rule has a pre-existing exception
            this.v$.$touch();
            this.alertOnInvalidFields(this.v$, ev, reconfirmFromDropdown);
            if (!this.v$.$invalid) {
                const createdDate = new Date(this.getCurrentOverride.created);
                let override = Object.assign({}, this.getCurrentOverride);
                override["username"] = this.getScenarioByName(this.scenarioName).username;
                override["userId"] = this.getScenarioByName(this.scenarioName).userId;
                override["created"] = createdDate.valueOf();
                // if not a legacy loan then push these fields
                if (!this.getIsLegacyLoan) {
                    const grantedByDate = new Date(this.getCurrentOverride.grantedByDate);
                    override["grantedByDate"] = grantedByDate.valueOf();
                }
                override["confirmed"] = true;
                this.setRuleOverride(override);
                this.updateSelectedRule();
                this.setSaved(false);
            }
        },
        tableRowClassName({row}) {
            let rowClass = '';
            if (!row.valid) {
                rowClass = 'inner-fail';
            }
            return rowClass;
        },
        getCurrentDate(override) {
            // gets today's date in the local string format
            let date;
            if (override) {
                date = new Date(override.created);
            } else {
                date = new Date(Date.now());
            }
            return date.toLocaleDateString('fr-CA');
        },
        toggleShowPassing() {
            // used for testing
            this.showPassing = !this.showPassing;
        },

        

        getIcon(status) {
            // sets the icon to be displayed with status text in the table
            let thisClass = "";
            if (status === "Passed") {
                thisClass = "fas fa-check fa-md";
            } else if (status === "Exception") {
                thisClass = "fas fa-dot-circle fa-md";
            } else if (status === "Reconfirm") {
                thisClass = "fas fa-question fa-md";
            } else if (status === "Insufficient factors") {
                thisClass = "fas fa-exclamation fa-md";
            } else {
                thisClass = "fas fa-times fa-lg";
            }
            return thisClass;
        },
        checkUserPermissions(row) {
            let ableToMakeException = false;
            // if the loan was opened in QDU after the legacy cutoff date
            if (row.exceptionLevel && !this.getIsLegacyLoan) {
                // if the rule's exception level is 1, no requirements
                if (row.exceptionLevel === 1) {
                    ableToMakeException = true;
                // if the rule has 2 or more comp factors
                } else if (this.getPassingCompFactorLength >= 2) {
                    // the row's exception level is 2, needs 2 or more comp factors and the user is an underwriter or manager
                    if (row.exceptionLevel === 2 && this.userIsUnderwriter) {
                        ableToMakeException = true;
                    // the rule's exception level is 3, needs manager approval and the user has a manager persona
                    } else if (row.exceptionLevel === 3 && this.userIsManager) {
                        ableToMakeException = true;
                    }
                } 
            // if the loan is a legacy loan
            } else {
                ableToMakeException = true;
            }
            return ableToMakeException;
        },
        getPopoverText(rule) {
            let text;
            if (!this.getHasRun) {
                text = 'The scenario must be run in order for exceptions to be made.'
            } else if (rule.exceptionLevel === 3 && !this.userIsManager) {
                text = 'Only managers are allowed to make an exception for this rule.'
            } else if (rule.exceptionLevel === 2 && !this.userIsUnderwriter) {
                text = 'Only managers and underwriters are allowed to make an exception for this rule.'
            } else if (this.getPassingCompFactorLength < 2) {
                text = 'Two compensating factors are required to edit exceptions.'
            }
            return text;
        },
        getOkButtonText(override) {
            let text;
            // if the rule has an existing exception
            if (override) {
                // if the exception does not need to be reconfirmed
                if (override.confirmed) {
                    // ok button text should be 'Overwrite'
                    text = 'Overwrite';
                // if the exception needs to be reconfirmed
                } else {
                    // ok button text should be 'Reconfirm'
                    text = 'Reconfirm';
                }
            // if the rule does not have an existing exception
            } else {
                text = 'Confirm';
            }
            return text;
        }
    },
    validations() {
        return {
            getCurrentOverride: {
                comment: {
                    required: requiredIf(function () {
                        return this.getCurrentOverride.created !== null;
                    })
                },
                category: {
                    required: requiredIf(function() {
                        return this.getSelectedRule.exceptionLevel >= 2
                            && this.getCurrentOverride.created !== null;
                    })
                },
                type: {
                    required: requiredIf(function() {
                        return this.getSelectedRule.exceptionLevel >= 2
                            && this.getCurrentOverride.created !== null;
                    })
                },
                grantedByDate: {
                    required: requiredIf(function() {
                        return this.getSelectedRule.exceptionLevel >= 2
                            && this.getCurrentOverride.created !== null;
                    })
                },
                grantedByUsername: {
                    required: requiredIf(function() {
                        return this.getSelectedRule.exceptionLevel >= 2
                            && this.getCurrentOverride.created !== null;
                    })
                },
            }
        };
    },
    mixins: [ruleDialog],
};
</script>

<style>
.el-table .cell{
    padding-left: 0px;
    padding-right: 0px;
    word-break: break-word;
    font-size: 14px;
}

.el-popover {
    word-break: break-word;
    text-align: initial;
    min-width: 0;
}

.table-row-name {
    font-weight: 600;
}

.expanded {
    background-color: #f5f5f5 !important;
}

.allow-new-lines {
    white-space: pre-line;
}
.label {
    font-size: 15px;
}

.pass {
    color: rgb(30, 151, 30);
    font-weight: bold;
}

.fail {
    color: red;
    font-weight: bold;
}

.push-left {
    padding-right: 2%;
}

.inner-fail {
    background-color: rgba(255, 0, 0, .10) !important;
}

body,
.modal,
.fade {
    padding-right: 0px !important;
}
</style>

<style lang="scss">
.slide-in {
    &-move {
        transition: opacity 0.15s linear, transform 0.15s ease-in-out;
    }

    &-leave-active {
        transition: opacity 0.15s linear, transform 0.15s cubic-bezier(0.5, 0, 0.7, 0.4); //cubic-bezier(.7,0,.7,1);
    }

    &-enter-active {
        transition: opacity 0.15s linear, transform 0.15s cubic-bezier(0.2, 0.5, 0.1, 1);
        transition-delay: calc(0.25s);
    }

    &-enter,
    &-leave-to {
        opacity: 0;
    }
}
</style>


